import EmailViewerDialog from 'components/ReusableComponents/EmailViewerDialog'
import EmailIcon from 'components/ReusableComponents/Icons/Email'
import ConversationMedia from 'components/ReusableComponents/Messaging/ConversationBubble/ConversationMedia'
import moment from 'moment'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import MobileLink from '../../MobileLink/index'
import RatingStars from '../../RatingStars'
import styles from './ConversationBubble.module.scss'
import DeliveryStatus from './DeliveryStatus'
import Initials from './Initials'

const directbotBotRegex = /automation directful|directbot/i
const linkRegex =
  /((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*))/gi
const EmailBubble = ({
  starter,
  noInitials,
  senderName,
  isSenderClient,
  finalizedOn,
  sentOn,
  scheduledOn,
  children,
  shouldApplyOffset,
  emailDetails: { providerMessageId, from },
  chatId
}) => {
  const [isShowingEmail, setIsShowingEmail] = useState(false)

  const {
    currentUtcOffset,
    currentUser: { name: currentUserName }
  } = useSelector(s => s.login)

  function formatDate(date) {
    if (shouldApplyOffset) {
      return moment(date).utcOffset(currentUtcOffset).format('h:mm A')
    } else {
      return moment(date).format('h:mm A')
    }
  }

  return (
    <>
      {isShowingEmail && (
        <EmailViewerDialog
          onClose={() => setIsShowingEmail(false)}
          chatId={chatId}
          providerMessageId={providerMessageId}
        />
      )}
      <div
        className={[
          isSenderClient ? styles.containerPrimary : styles.containerSecondary
        ].join(' ')}
      >
        <div className={styles.initialsHolder}>
          {starter && !noInitials && <Initials senderName={senderName} />}
        </div>
        <div className={styles.contentHolder}>
          {children && (
            <div>
              <div className={starter ? styles.starterMain : styles.main}>
                <div
                  style={{ minWidth: 250 }}
                  className={
                    starter
                      ? styles.starterBubble
                      : noInitials
                      ? styles.bubble
                      : styles.bubbleWithMargin
                  }
                >
                  <EmailIcon
                    className={styles.emailIcon}
                    style={{ fill: isSenderClient ? '#FFFFFF' : '#66BB6A' }}
                  />
                  {senderName === currentUserName && (
                    <div className={styles.name}>You</div>
                  )}
                  <div className={styles.time}>
                    {formatDate(finalizedOn || sentOn || scheduledOn)}
                  </div>
                  <div className={styles.emailFrom}>
                    <span className={styles.emailFromLabel}>from</span>
                    {from}
                  </div>
                  <div className={styles.emailSubject}>{children}</div>
                  <button
                    style={{
                      color: isSenderClient ? 'rgb(255 255 255 / 68%)' : '#0079ff'
                    }}
                    onClick={() => setIsShowingEmail(true)}
                    className={styles.viewEmailButton}
                  >
                    View full email →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const ConversationBubble = props => {
  const {
    senderName,
    scheduledOn,
    sentOn,
    finalizedOn,
    children,
    isSenderClient,
    shouldApplyOffset,
    starter,
    deliveryStatus,
    deliveryError,
    isLastMessage,
    noInitials,
    feedback,
    mediaCount,
    messagingType,
    chatId,
    chatMessageId,
    category,
    chatMessageKind
  } = props

  const [hovering, setHovering] = useState(false)

  const {
    currentUtcOffset,
    currentUser: { name: currentUserName },
    isDemoMode
  } = useSelector(s => s.login)

  const hotelNamePlaceholder = 'The Luxury Resort & Spa'
  const hotelName = useSelector(s => s.hotelDetails.name)

  function formatDate(date) {
    if (shouldApplyOffset) {
      return moment(date).utcOffset(currentUtcOffset).format('h:mm A')
    } else {
      return moment(date).format('h:mm A')
    }
  }

  const processMessageContent = content => {
    if (!content) return

    if (isDemoMode && hotelName) {
      const hotelNameRegex = new RegExp(hotelName, 'gi')
      content = content.replace(hotelNameRegex, hotelNamePlaceholder)
    }

    return content
      .replaceAll(linkRegex, '⌘⌘$1⌘⌘')
      .split('⌘⌘')
      .map(p =>
        !p.includes('@') && p.match(linkRegex) ? (
          <MobileLink key={p} href={p.includes('http') ? p : `https://${p}`}>
            {p.replace('https://', '').replace('http://', '')}
          </MobileLink>
        ) : (
          p
        )
      )
  }

  if (!(mediaCount > 0 || children || feedback)) return null

  if (chatMessageKind === 'Email') {
    return <EmailBubble {...props} />
  }
  return (
    <div
      className={[
        isSenderClient ? styles.containerPrimary : styles.containerSecondary,
        mediaCount > 0 ? styles.containerWithMedia : ''
      ].join(' ')}
      onMouseOver={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className={styles.initialsHolder}>
        {starter && !noInitials && <Initials senderName={senderName} />}
      </div>
      <div className={styles.contentHolder}>
        {mediaCount > 0 && (
          <ConversationMedia
            mediaCount={mediaCount}
            messagingType={messagingType}
            chatId={chatId}
            messageId={chatMessageId}
            isSenderClient={isSenderClient}
          />
        )}
        {children && (
          <div>
            <div className={starter ? styles.starterMain : styles.main}>
              <div
                className={
                  starter
                    ? styles.starterBubble
                    : noInitials
                    ? styles.bubble
                    : styles.bubbleWithMargin
                }
              >
                {senderName !== currentUserName && !isSenderClient && (
                  <>
                    {senderName?.match(directbotBotRegex) ? (
                      <div className={styles.botName}>DirectBot</div>
                    ) : (
                      <div className={styles.name}>{senderName}</div>
                    )}
                  </>
                )}
                {senderName === currentUserName && <div className={styles.name}>You</div>}
                <div className={styles.time}>
                  {formatDate(finalizedOn || sentOn || scheduledOn)}
                </div>
                <div className={styles.text}>{processMessageContent(children)}</div>
              </div>
            </div>
            <DeliveryStatus
              deliveryStatus={deliveryStatus}
              isSenderClient={isSenderClient}
              isLastMessage={isLastMessage}
              deliveryError={deliveryError}
              hovering={hovering}
              category={category}
              sentOn={sentOn}
              shouldApplyOffset={shouldApplyOffset}
            />
          </div>
        )}
        {feedback && (
          <div className={styles.review}>
            <div className={styles.reviewTitle}>
              {senderName} left a {feedback?.reviewRating}-star review!
            </div>
            <div className={styles.reviewStars}>
              <RatingStars rating={feedback?.reviewRating} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationBubble
